import { useEffect, useRef } from 'react';
import { useSyncExternalStore } from 'react';
import {
    announcerSupported,
    isAnnouncerEnabled,
    setAnnouncerEnabled,
    subscribeToAnnouncer,
} from '../lib/announcer';
import type { Market } from '../schemas/market.schema';

const speak = (text: string): void => {
    if (!('speechSynthesis' in window)) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.05;
    window.speechSynthesis.speak(utterance);
};

/**
 * The engine. Mount once, high up, where the market feed is already polled.
 *
 * Driven by comparing successive snapshots rather than by events, because the
 * app already polls and a missed event would be a silent announcer.
 */
export const useAnnouncerEngine = (markets: Market[] | undefined): void => {
    const enabled = useSyncExternalStore(
        subscribeToAnnouncer,
        isAnnouncerEnabled,
        isAnnouncerEnabled,
    );

    const seen = useRef(new Map<number, Market['status']>());
    const warned = useRef(new Set<number>());
    // Nothing is announced from the first snapshot, or switching it on would
    // read out the whole night at once.
    const primed = useRef(false);

    useEffect(() => {
        if (!enabled) primed.current = false;
    }, [enabled]);

    useEffect(() => {
        if (!markets) return;

        if (!primed.current) {
            for (const market of markets) seen.current.set(market.id, market.status);
            primed.current = true;
            return;
        }

        for (const market of markets) {
            const previous = seen.current.get(market.id);
            seen.current.set(market.id, market.status);

            if (!enabled) continue;

            if (previous === undefined) {
                speak(`New bet. ${market.question}`);
                continue;
            }

            if (previous !== 'settled' && market.status === 'settled') {
                const winner = market.outcomes.find((o) => o.id === market.resolvedOutcomeId);
                speak(
                    winner
                        ? `${market.question} ${winner.label}. ${market.pot} tokens.`
                        : `${market.question} settled.`,
                );
                continue;
            }

            const remaining = market.closesAt.getTime() - Date.now();
            if (
                market.status === 'open' &&
                remaining > 0 &&
                remaining < 35_000 &&
                !warned.current.has(market.id)
            ) {
                warned.current.add(market.id);
                speak(`Thirty seconds on ${market.question}`);
            }
        }
    }, [markets, enabled]);
};

/** The switch, for wherever it is shown. */
export const useAnnouncerSetting = () => {
    const enabled = useSyncExternalStore(
        subscribeToAnnouncer,
        isAnnouncerEnabled,
        isAnnouncerEnabled,
    );
    return { enabled, setEnabled: setAnnouncerEnabled, supported: announcerSupported() };
};
